import { type PlanogramRenderedProduct } from "@augmodo/reusable-components";
import { mvpApiCreateProductIssueMutation } from "@labeling-tools/api/atlas/@tanstack/react-query.gen";
import { formatMoney } from "@labeling-tools/ui-components/src/utils/helpers";
import { Button } from "@mantine/core";
import { IconPhotoOff } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";

interface Props {
  item: PlanogramRenderedProduct;
}

/**
 * Click-popover body (admin/read-only view) — feature parity with the legacy
 * ActivePlanoProductPopover card: pack shot, description, facings, price and
 * the "Report Bad Image" action. No fetch needed: unlike the legacy popover
 * (which only had ids), the reusable viewer's item carries every field the
 * card shows. Mounted only while open, so the report state resets per open,
 * same as the legacy card (it remounted per product).
 */
export const PlanogramProductPopoverContent = ({ item }: Props) => {
  const {
    mutate: reportBadImage,
    isPending: isReporting,
    isSuccess: isReported,
    isError: isReportError,
  } = useMutation(mvpApiCreateProductIssueMutation());

  return (
    <div className="w-56 text-center">
      <div className="flex justify-center">
        <div className="size-36 bg-white flex items-center justify-center">
          {item.imageUrl ? (
            <img
              src={item.imageUrl}
              className="size-full object-contain"
              alt={item.label || "Product"}
            />
          ) : (
            <IconPhotoOff size={24} color="#9ca3af" />
          )}
        </div>
      </div>
      <div className="h-4" />
      <div className="space-y-2 text-xs">
        {!!item.label && <div className="font-semibold">{item.label}</div>}
        <div className="text-gray-500">
          Facings: {item.product.facings ?? 0}
        </div>
        {typeof item.product.price === "number" && (
          <div className="text-gray-500">
            Price: {formatMoney(item.product.price)}
          </div>
        )}
        <div>
          <Button
            color="red"
            variant="transparent"
            size="xs"
            onClick={() =>
              reportBadImage({
                body: {
                  type: "bad_image",
                  planogram_product_id: item.product.id,
                  product_id: item.product.product_id,
                },
              })
            }
            loading={isReporting}
            disabled={isReported}
          >
            {isReported
              ? "Reported"
              : isReportError
                ? "Failed - Try Again"
                : "Report Bad Image"}
          </Button>
        </div>
      </div>
    </div>
  );
};
